import React, { useEffect, useRef } from 'react';
import { loadMapbox } from '../../../utils/lazyMapbox';
import { MAPBOX_CONFIG } from '../../../config/mapbox';
import { formatAddressOneLine } from '../utils/addressUtils';

/**
 * AddressPreview component for displaying an address with optional map
 * Integrates with existing address format in the app
 */
const AddressPreview = ({
  address,
  showMap = true,
  interactive = false,
  mapHeight = '200px',
  className = '',
  onEdit = null,
  onDelete = null,
  onSetDefault = null,
  isDefault = false
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  
  // Initialize or update map when address coordinates change
  useEffect(() => {
    if (!showMap || !address?.latitude || !address?.longitude || !mapContainer.current) {
      return;
    }
    
    let cancelled = false;

    loadMapbox().then((mapboxgl) => {
      if (cancelled || !mapContainer.current) return;

      if (!map.current) {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: MAPBOX_CONFIG.defaultMapStyle,
          center: [address.longitude, address.latitude],
          zoom: MAPBOX_CONFIG.defaultZoom,
          interactive: interactive
        });
        
        new mapboxgl.Marker({ color: MAPBOX_CONFIG.markerColor })
          .setLngLat([address.longitude, address.latitude])
          .addTo(map.current);
      } else {
        map.current.setCenter([address.longitude, address.latitude]);
      }
    });
    
    return () => {
      cancelled = true;
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [address?.latitude, address?.longitude, showMap, interactive]);
  
  // If no address, show placeholder
  if (!address) {
    return (
      <div className={`rounded-md overflow-hidden border border-gray-300 ${className}`}>
        <div className="p-4 bg-gray-50 text-gray-500 text-center">
          No address selected
        </div>
      </div>
    );
  }
  
  // Format address for display depending on available fields
  let addressText = '';
  if (typeof formatAddressOneLine === 'function') {
    addressText = formatAddressOneLine(address);
  } else {
    addressText = [
      address.street,
      address.apartment,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean).join(', ');
  }
  
  return (
    <div className={`rounded-md overflow-hidden border border-gray-300 ${className}`}>
      {/* Name and default badge */}
      {address.name && (
        <div className="px-3 py-2 bg-gray-100 border-b border-gray-300 flex items-center justify-between">
          <span className="font-medium">{address.name}</span>
          {isDefault && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-teal text-white">
              Default
            </span>
          )}
        </div>
      )}
      
      {/* Map preview */}
      {showMap && address.latitude && address.longitude && (
        <div 
          ref={mapContainer}
          className="w-full"
          style={{ height: mapHeight }}
        />
      )}
      
      {/* Address details */}
      <div className="p-3 bg-gray-50">
        <p className="text-sm text-gray-700">{addressText}</p>
        
        {address.phoneNumber && (
          <p className="mt-1 text-sm text-gray-600">
            Phone: {address.phoneNumber}
          </p>
        )}
        
        {address.instructions && (
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-medium">Instructions:</span> {address.instructions}
          </p>
        )}
        
        {/* Action buttons */}
        {(onEdit || onDelete || onSetDefault) && (
          <div className="mt-3 flex space-x-2 pt-2 border-t border-gray-200">
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(address)}
                className="text-sm text-brand-teal hover:text-teal-700"
              >
                Edit
              </button>
            )}
            
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(address)}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            )}
            
            {onSetDefault && !isDefault && (
              <button
                type="button"
                onClick={() => onSetDefault(address.id)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Set as default
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressPreview;